import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquareText, SendHorizonal } from "lucide-react";
import api, { fmtDate, formatApiErrorDetail } from "@/lib/api";

export const ChatThread = ({ messages, mine, testId }) => {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages.length]);
  return (
    <div className="h-[320px] overflow-y-auto px-5 py-4 space-y-3 bg-black/40" data-testid={testId}>
      {messages.length === 0 && <p className="text-sm text-white/40 text-center pt-16" data-testid={`${testId}-empty`}>No messages yet.</p>}
      {messages.map((m) => {
        const own = m.sender === mine;
        return (
          <div key={m.id} className={`flex ${own ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.sender}`}>
            <div className={`max-w-[78%] px-4 py-2.5 text-sm leading-relaxed ${own ? "bg-[#D4AF37] text-black" : "bg-white/[0.06] text-white border border-white/10"}`}>
              <p className="whitespace-pre-wrap break-words">{m.text}</p>
              <p className={`text-[10px] mt-1 ${own ? "text-black/60" : "text-white/35"}`}>{m.sender === "admin" ? "MetrixWorks desk · " : ""}{fmtDate(m.created_at)}</p>
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
};

export const ChatComposer = ({ onSend, placeholder, testId }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await onSend(text.trim());
      setText("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex gap-2 p-3 border-t border-white/10">
      <textarea
        data-testid={`${testId}-input`}
        rows={1}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); } }}
        placeholder={placeholder}
        maxLength={2000}
        className="flex-1 bg-transparent border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-white/30 gold-focus resize-none"
      />
      <button data-testid={`${testId}-send`} disabled={busy || !text.trim()} className="border border-[#D4AF37] text-[#D4AF37] px-4 font-accent text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-40 flex items-center gap-2">
        <SendHorizonal size={13} /> Send
      </button>
    </form>
  );
};

export const SupportChat = () => {
  const [messages, setMessages] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/support/messages").then((r) => setMessages(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);
  const send = async (text) => {
    const { data } = await api.post("/support/messages", { text });
    setMessages((m) => [...m, data]);
    toast.success("Message sent — our desk will reply here and by email");
  };
  const unread = messages.filter((m) => m.sender === "admin" && !m.read).length;
  return (
    <section className="mt-6 border border-white/10 bg-[#0A0A0A]" data-testid="support-chat">
      <button data-testid="support-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-6 py-5 text-left">
        <span className="flex items-center gap-3">
          <MessageSquareText size={16} className="text-[#D4AF37]" />
          <span>
            <span className="block text-sm text-white">Ask the MetrixWorks desk</span>
            <span className="block text-xs text-white/40">Questions about plans, payments or payouts — we reply here and by email.</span>
          </span>
        </span>
        <span className="font-accent text-[10px] tracking-[0.2em] uppercase text-white/60 border border-white/20 px-4 py-2">{open ? "Close" : messages.length ? `Open chat (${messages.length})` : "Start chat"}{unread ? ` · ${unread} new` : ""}</span>
      </button>
      {open && (
        <div className="border-t border-white/10">
          <ChatThread messages={messages} mine="investor" testId="support-thread" />
          <ChatComposer onSend={send} placeholder="Type your question… (Enter to send)" testId="support-composer" />
        </div>
      )}
    </section>
  );
};
